import { Banner, Button, Card, Modal, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  deleteSpaceMutation,
  errorMessage,
  listSpacesQueryKey,
  type SpaceHubResponse,
  updateSpaceMutation,
} from "@/api";
import { BannerAction } from "@/components/BannerAction";
import { useDeleteEntity } from "@/components/build/useDeleteEntity";
import { Text } from "@/components/Text";
import { useRefreshSpace } from "./useSpaceSettings";

function useSetStatus(space: SpaceHubResponse) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings" });
  const toast = useToast();
  const refresh = useRefreshSpace(space.uuid);
  const update = useMutation(updateSpaceMutation());
  return {
    pending: update.isPending,
    set: async (status: "active" | "archived") => {
      try {
        await update.mutateAsync({ path: { space_uuid: space.uuid }, body: { status } });
        await refresh();
        toast.success(t(status === "archived" ? "danger.archived" : "archived.restored"));
      } catch (error) {
        toast.error(errorMessage(error, t("danger.failed")));
      }
    },
  };
}

/** Shown on an archived space's settings; Restore brings it back (admin). */
export function ArchivedBanner({ space }: Readonly<{ space: SpaceHubResponse }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.archived" });
  const status = useSetStatus(space);
  return (
    <Banner
      variant="warning"
      message={t("message")}
      action={space.canManage && <BannerAction onClick={() => void status.set("active")}>{t("restore")}</BannerAction>}
    />
  );
}

/** Archive (reversible) and delete (permanent), for space admins. */
export function DangerZone({ space }: Readonly<{ space: SpaceHubResponse }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.danger" });
  const navigate = useNavigate();
  const status = useSetStatus(space);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const del = useDeleteEntity<{ uuid: string; name: string }>({
    mutation: deleteSpaceMutation(),
    listQueryKey: listSpacesQueryKey,
    pathFor: (s) => ({ space_uuid: s.uuid }),
    messages: { deleted: t("deleted"), deleteFailed: t("deleteFailed") },
    onDeleted: () => navigate("/spaces"),
  });
  const archived = space.status === "archived";

  return (
    <Card className="flex flex-col gap-4 border-error-subtle">
      <Text as="h2" variant="h3">
        {t("title")}
      </Text>
      {!archived && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Text variant="body-sm" tone="secondary" className="max-w-md">
            {t("archiveBody")}
          </Text>
          <Button variant="secondary" size="sm" onClick={() => setConfirmArchive(true)}>
            {t("archive")}
          </Button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Text variant="body-sm" tone="secondary" className="max-w-md">
          {t("deleteBody")}
        </Text>
        <Button variant="danger" size="sm" onClick={() => del.onDelete({ uuid: space.uuid, name: space.name })}>
          {t("delete")}
        </Button>
      </div>
      {confirmArchive && (
        <Modal
          open
          onClose={() => setConfirmArchive(false)}
          title={t("archiveConfirmTitle", { name: space.name })}
          actions={
            <>
              <Button variant="secondary" onClick={() => setConfirmArchive(false)}>
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                loading={status.pending}
                onClick={async () => {
                  await status.set("archived");
                  setConfirmArchive(false);
                }}
              >
                {t("archiveConfirm")}
              </Button>
            </>
          }
        >
          <Text variant="body-sm" tone="secondary">
            {t("archiveBody")}
          </Text>
        </Modal>
      )}
      {del.dialog}
    </Card>
  );
}
